import { useParams } from "react-router-dom";
import {
  TextField,
  NumberField,
  SelectField,
  useStore,
  useRecordContext,
  Link,
  WithRecord,
  TopToolbar,
  useResourceContext,
  ListButton,
} from "react-admin";
import { statusChoices, accessModeChoices } from "./base";
import EventsList from "../events";
import CommandsTable from "../commands";

import { StatusHeader, TowerOrganization } from "./components";

import { TowerHistorySummaryField, TowerHistoryList } from "../towerHistory";
import { LockerList } from "../lockers";
import { Box, CardHeader } from "@mui/material";
import { RecordSummary, MyShow } from "../../ra-lb-tools/components/MyShow.js";
import { LocationField } from "../locations/components";
import { KeyList } from "../keys";
import { ReservationList } from "../reservations";
import { OpenLockerTokenList as OpenLockerTokenList } from "../openLockerTokens";
import { SystemField } from "../systems";
import { useState } from "react";
import { ActiveLockerUsageList, LockerUsageList } from "../billing/lockerUsage";
import { LabeledColumn } from "../../ra-lb-tools/components/labeledColumn.js";
import { Row } from "../../ra-lb-tools/components/row.js";
import { MyBooleanField as BooleanField } from "../../components/myBooleanField";
import { TowerRenderByTowerId } from "../tower_render/TowerRenderByTowerId";
import { Column } from "../../ra-lb-tools/components/column";
import { DeviceCurrentTowersCount } from "../devices";
import { DeviceHistoryList } from "../deviceHistory";
import { GoBackButton } from "../../ra-lb-tools/components/goBackButton";
import { Edit, MoreVert, Wifi } from "@mui/icons-material";
import { MyDateField } from "../../ra-lb-tools/components/myDateField";
import { MyEditButton as EditButton } from "../../ra-lb-tools/components/myEditButton";
import { WithPermissions } from "../../ra-lb-tools/acl/withPermissions";
import {
  MyTabbedShowLayout,
  Tab,
} from "../../ra-lb-tools/components/myTabbedShowLayout";
import { MyReferenceField } from "../../ra-lb-tools/components/myReference";
import { UserField } from "../users";
import { LinkButton } from "../../ra-lb-tools/components/linkButton";
import { toQueryString } from "../../ra-lb-tools/utils/tools";
import { LastReferenceField } from "../../ra-lb-tools/components/lastReferenceField";
import { OrgLockersList } from "../org/org_lockers";
import {
  ConfigTowerButton,
  FactoryResetTowerButton,
  ReleaseLockersButton,
  ResetTowerButton,
  SyncTowerButton,
} from "./buttons";
import { OnlineStatusField } from "./towers";
import { LockerHistoryList } from "../lockerHistory";
import { ActionsDrawer } from "./actionsDrawer";
import { WithOnlineHardware } from "./withOnlineHardware";

const TowerLastBoot = () => {
  return (
    <LastReferenceField
      reference="events"
      source="towerId"
      sort={{ field: "received", order: "DESC" }}
      filter={{ packet_type: 20 }}
      noDataMsg="No data"
      render={(event, tower) => {
        const query = toQueryString({ events: { filter: { id: event.id } } });
        const path = `/towers/${tower.id}/show/events?${query}`;
        return (
          <Link to={path}>
            <MyDateField
              record={event}
              source="eventData.packet_timestamp"
              showTime
            />
          </Link>
        );
      }}
    />
  );
};

const TowerLastReset = () => {
  return (
    <LastReferenceField
      reference="tower-history"
      source="towerId"
      filter={{ type: [5, 6] }}
      noDataMsg="No data"
      render={(towerHistory, tower) => {
        const query = toQueryString({
          "tower-history": { filter: { id: towerHistory.id } },
        });
        const path = `/towers/${tower.id}/show/history?${query}`;
        return (
          <Link to={path}>
            <MyDateField record={towerHistory} source="timestamp" showTime />
          </Link>
        );
      }}
      noPermissions={<MyDateField source="lastEventsReset" showTime={true} />}
    />
  );
};

const BoardInfo = () => {
  return (
    <WithRecord
      render={(record) => (
        <Box className="BoardInfo">
          <Link
            to={
              record.deviceSerial
                ? `/devices/${record.deviceSerial}/show`
                : null
            }
          >
            <TextField source="deviceSerial" />
          </Link>
          {record.deviceSerial && (
            <DeviceCurrentTowersCount source="deviceSerial" />
          )}
        </Box>
      )}
    />
  );
};

const EditIsOnlineButton = () => {
  const record = useRecordContext();

  if (!record) return null;
  const path = `/towers/${record.id}/is-online`;

  return (
    <WithPermissions can={{ resource: "towers", action: "update_online" }}>
      <LinkButton
        to={path}
        label={"Online"}
        onClick={(e) => e.stopPropagation()}
      >
        <Edit />
      </LinkButton>
    </WithPermissions>
  );
};

const EditModelButton = () => {
  const record = useRecordContext();

  if (!record) return null;
  if (record.lockersCount) return null;
  const path = `/towers/${record.id}/model`;

  return (
    <WithPermissions can={{ resource: "towers", action: "update_tower_model" }}>
      <LinkButton
        to={path}
        label={"Model"}
        onClick={(e) => e.stopPropagation()}
      >
        <Edit />
      </LinkButton>
    </WithPermissions>
  );
};

export const EditStatusButton = ({ noLabel }) => {
  const record = useRecordContext();

  if (!record) return null;
  const path = `/towers/${record.id}/status`;

  return (
    <WithPermissions can={{ resource: "towers", action: "update_status" }}>
      <LinkButton
        className="EditStatusButton"
        to={path}
        label={noLabel ? "" : "Status"}
        onClick={(e) => e.stopPropagation()}
      >
        <Edit />
      </LinkButton>
    </WithPermissions>
  );
};

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <EditModelButton />
    <EditStatusButton />
    <WithOnlineHardware>
      <ActionsDrawer
        title="Online Management"
        startIcon={<Wifi />}
        endIcon={<MoreVert />}
        label=""
        sx={{ "[class^='MuiButton-'][class$='Icon']": { margin: 0 } }}
      >
        <EditIsOnlineButton />
        <SyncTowerButton />
        <ConfigTowerButton />
        <FactoryResetTowerButton />
      </ActionsDrawer>
    </WithOnlineHardware>
    <GoBackButton size="small" variant="text" />
  </TopToolbar>
);

export const TowerShow = () => {
  const { id } = useParams();
  const resource = useResourceContext();
  const lockerResource = resource == "org-towers" ? "org-lockers" : "lockers";

  const LockerListComp =
    lockerResource == "org-lockers" ? OrgLockersList : LockerList;

  // Tower Render
  const [renderAlwaysVisible, setRenderAlwaysVisible] = useStore(
    "tower-render-always-visible",
    false
  );
  const [highlightList, setHighlightList] = useState([]);
  const [rowRef, setRowRef] = useState({});

  const addRowRef = (locker, ref) => {
    const key = locker.physicalId;
    setRowRef((rowRef) => {
      rowRef[key] = ref;
      return rowRef;
    });
  };

  const scrollToRow = (locker) => {
    const key = locker.physicalId;

    rowRef[key]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const dataColumnSx = renderAlwaysVisible ? { width: "70%" } : {};
  const fullHeighClass = renderAlwaysVisible ? "h-100-overflow" : "";

  const TowerRender = (
    <WithPermissions
      can={[
        { resource: resource, action: "show" },
        { resource: lockerResource, action: "list" },
        { resource: "locker-status", action: "read_all" },
        { resource: "locker-types", action: "read_all" },
        { resource: "tower-models", action: "show" },
      ]}
      label={"TowerShow.TowerRender"}
    >
      <TowerRenderByTowerId
        {...{
          towerId: id,
          highlightList,
          setHighlightList,
          renderAlwaysVisible,
          setRenderAlwaysVisible,
          onClick: scrollToRow,
          columnProps: {
            className: fullHeighClass,
          },
        }}
      />
    </WithPermissions>
  );

  const dataClassName = [fullHeighClass].filter((v) => v).join(" ");

  return (
    <Row
      className={[
        "TowerShow",
        fullHeighClass,
        renderAlwaysVisible ? "tower-render-visible" : "",
      ].join(" ")}
    >
      <MyShow
        className={dataClassName}
        style={dataColumnSx}
        actions={<ShowActions />}
      >
        <MyTabbedShowLayout>
          <Tab label="Summary" can={true} key="summary">
            <RecordSummary Component={Box}>
              <Column>
                <Row>
                  <LabeledColumn>
                    <TextField source="id" />
                    <TextField source="name" />
                    <TextField source="pinCode" />
                    <TextField source="description" />
                    <TextField source="privateNotes" />
                    <BooleanField source="hasKeypad" />
                    <MyReferenceField
                      source="towerModelId"
                      reference="tower-models"
                      link="show"
                    />
                    <BooleanField source="isForTest" />
                  </LabeledColumn>
                  <LabeledColumn>
                    <SystemField label="System" source="systemId" />
                    <LocationField label="Location" source="locationId" />
                    <TextField source="location.city" label="City" />
                    <TextField source="location.state" label="State" />
                    <SelectField
                      source="statusId"
                      choices={statusChoices}
                      label={<StatusHeader />}
                    />
                    <SelectField
                      source="accessModeId"
                      choices={accessModeChoices}
                    />
                  </LabeledColumn>
                  <LabeledColumn>
                    <NumberField source="lastEvent" />
                    <NumberField source="lastCommand" />
                    <UserField source="ownerId" />
                    <TextField source="fwVersion" />
                    <TextField source="blVersion" label="Bootloader Version" />
                    <TextField source="deviceModel" />
                    <BoardInfo source="deviceSerial" />
                  </LabeledColumn>
                  <LabeledColumn>
                    <TowerLastReset source="lastEventsReset" />
                    <MyDateField source="lastEventsSync" showTime={true} />
                    <Box
                      source="lastSync"
                      label="Last Sync Status"
                      sx={{
                        ".TowerHistorySummary.status-failed": {
                          fontSize: "4.5em !important",
                          backgroundColor: "rgba(255, 0, 0, .2)",
                        },
                      }}
                    >
                      <WithRecord
                        render={(tower) => (
                          <TowerHistorySummaryField record={tower.lastSync} />
                        )}
                      />
                    </Box>
                    <TowerLastBoot label="Last Boot" source="lastBoot" />
                    <MyDateField source="configuredAt" />
                    <TowerOrganization
                      source="__towerOrganization"
                      label="Organization"
                    />
                    <OnlineStatusField source="onlineStatus" />
                    <BooleanField source="fullConfigured" />
                  </LabeledColumn>
                  {!renderAlwaysVisible && TowerRender}
                </Row>
                <LockerListComp
                  customHeader={
                    <CardHeader title="Lockers" sx={{ paddingBottom: 0 }} />
                  }
                  filter={{ towerId: id }}
                  filters={null}
                  rowProps={{
                    highlightList,
                    highlight_key: "physicalId",
                    setRowRef: addRowRef,
                  }}
                  customActions={[
                    <ReleaseLockersButton
                      resource={resource}
                      key="releaseLockers"
                    />,
                  ]}
                />
              </Column>
            </RecordSummary>
          </Tab>
          <Tab
            label="History"
            path="history"
            can={{ action: "list", resource: "tower-history" }}
            key="tower-history"
          >
            <TowerHistoryList filter={{ towerId: id }} />
          </Tab>
          <Tab
            label="Events"
            path="events"
            can={{
              action: "list",
              resource: "events",
            }}
            key="events"
          >
            <EventsList filter={{ towerId: id }} title="Tower Events" />
          </Tab>
          <Tab
            label="Commands"
            path="commands"
            can={{
              action: "list",
              resource: "commands",
            }}
            key="commands"
          >
            <CommandsTable filter={{ towerId: id }} title="Tower Commands" />
          </Tab>
          <Tab
            label="Keys"
            path="keys"
            can={{
              action: "list",
              resource: "keys",
            }}
            key="keys"
          >
            <KeyList filter={{ towerId: id }} />
          </Tab>
          <Tab
            label="Reservations"
            path="reservations"
            can={{
              action: "list",
              resource: "reservations",
            }}
            key="reservations"
          >
            <ReservationList filter={{ locker__tower_id: id }} />
          </Tab>
          <Tab
            label="Open Locker Token"
            path="open-locker-tokens"
            can={{
              action: "list",
              resource: "open-locker-tokens",
            }}
            key="open-locker-tokens"
          >
            <OpenLockerTokenList filter={{ locker__tower_id: id }} />
          </Tab>
          <Tab
            label="Active Locker Usage"
            path="active-locker-usage"
            can={{
              action: "list",
              resource: "locker-usage",
            }}
            key="locker-usage"
          >
            <ActiveLockerUsageList filter={{ locker__tower_id: id }} />
          </Tab>
          <Tab
            label="Locker Usage"
            path="locker-usage"
            can={{
              action: "list",
              resource: "locker-usage",
            }}
            key="locker-usage"
          >
            <LockerUsageList filter={{ locker__tower_id: id }} />
          </Tab>
          <Tab
            label="Devices"
            path="devices"
            can={{
              action: "list",
              resource: "devices",
            }}
            key="devices"
          >
            <DeviceHistoryList filter={{ towerId: id }} />
          </Tab>
          <Tab
            label="Locker History"
            path="locker-history"
            can={{
              action: "list",
              resource: "locker-history",
            }}
            key="locker-history"
          >
            <LockerHistoryList filter={{ towerId: id }} />
          </Tab>
          <ResetTowerButton skip-acl-check resource={resource} />
        </MyTabbedShowLayout>
      </MyShow>{" "}
      {renderAlwaysVisible && TowerRender}
    </Row>
  );
};
